import React, { useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQuery, useApolloClient } from '@apollo/react-hooks'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { CloudUpload, Trash2, X, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import * as Mutation from '../../../api/gql/mutation'
import * as Query from '../../../api/gql/query'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import { API_URL } from '../../../utils'
import { formatBytes } from '../../../utils/formatters'

const getFileUrl = (fileId: string) => `${API_URL}/download/${fileId}`

interface SnagFileUploadProps {
  snagId?: string
  existingFiles: any[]
  onFilesChange?: (fileIds: string[]) => void
  disabled?: boolean
}

const SnagFileUpload: React.FC<SnagFileUploadProps> = ({
  snagId,
  existingFiles = [],
  onFilesChange,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const apolloClient = useApolloClient()
  const [files, setFiles] = useState<any[]>(existingFiles || [])
  const [uploading, setUploading] = useState(false)
  const [previewImage, setPreviewImage] = useState<any>(null)

  const { data: meData } = useQuery(Query.ME)

  const [upload, uploadState] = useUpload() as any
  const { uploads } = uploadState

  const [createFile] = useMutation(Mutation.CREATE_FILE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED)
  const [deleteFile] = useMutation(Mutation.DELETE_FILE)
  const [setSnagFiles] = useMutation(Mutation.SET_SNAG_FILES)
  const [logSnagActivity] = useMutation(Mutation.LOG_SNAG_ACTIVITY)

  useEffect(() => {
    setFiles(existingFiles || [])
  }, [existingFiles])

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (disabled) return

      setUploading(true)
      try {
        const filesResult = await Promise.all(
          acceptedFiles.map(async (file) => {
            const { data } = await createFile({
              variables: {
                input: {
                  filename: file.name,
                  size: file.size,
                  mimetype: file.type || 'application/octet-stream',
                  encoding: 'utf8',
                  ...(snagId ? { snagId } : { sessionId: 'temp' }),
                },
              },
            })

            return {
              file,
              id: data.createFile.file.id,
              name: file.name,
              signedUrl: data.createFile.uploadUrl,
            }
          }),
        )

        const uploadedFileIds = await upload(filesResult)

        const { data: uploadedFilesData } = await setFileUploaded({
          variables: { ids: uploadedFileIds },
        })

        const newFiles = [...files, ...(uploadedFilesData?.setFileUploaded || [])]
        setFiles(newFiles)

        if (snagId) {
          await setSnagFiles({
            variables: {
              id: snagId,
              fileIds: newFiles.map((f) => f.id),
            },
          })

          if (meData?.me?.id && uploadedFilesData?.setFileUploaded) {
            for (const uploadedFile of uploadedFilesData.setFileUploaded) {
              await logSnagActivity({
                variables: {
                  snagId,
                  title: t('Snags_activity_file_uploaded'),
                  description: t('Snags_activity_uploaded_image_desc', { filename: uploadedFile.filename }),
                },
              })
            }
          }
        }

        if (onFilesChange) {
          onFilesChange(newFiles.map((f) => f.id))
        }
      } catch (error) {
        console.error('Error uploading files:', error)
      } finally {
        setUploading(false)
      }
    },
    [disabled, files, snagId, createFile, upload, setFileUploaded, setSnagFiles, onFilesChange, meData, logSnagActivity, t],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || uploading,
    accept: 'image/*',
  })

  const handleDeleteFile = async (fileId: string) => {
    if (disabled) return

    try {
      const fileToDelete = files.find((f) => f.id === fileId)

      await deleteFile({ variables: { ids: [fileId] } })

      const newFiles = files.filter((f) => f.id !== fileId)
      setFiles(newFiles)

      if (snagId) {
        await setSnagFiles({
          variables: { id: snagId, fileIds: newFiles.map((f) => f.id) },
        })

        if (meData?.me?.id && fileToDelete) {
          await logSnagActivity({
            variables: {
              snagId,
              title: t('Snags_activity_file_deleted'),
              description: t('Snags_activity_deleted_image_desc', { filename: fileToDelete.filename }),
            },
          })
        }
      }

      if (onFilesChange) {
        onFilesChange(newFiles.map((f) => f.id))
      }
    } catch (error) {
      console.error('Error deleting file:', error)
    }
  }

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          'rounded-md border-2 border-dashed p-6 text-center transition-colors cursor-pointer',
          isDragActive ? 'border-primary bg-primary/5' : 'border-border',
          (disabled || uploading) && 'opacity-50 cursor-not-allowed',
        )}
      >
        <input {...getInputProps()} />
        <CloudUpload className={cn('h-8 w-8 mx-auto mb-2', isDragActive ? 'text-primary' : 'text-muted-foreground')} />
        {isDragActive ? (
          <p className="text-sm mb-0">{t('Snags_modal_files_drop_images_here')}</p>
        ) : (
          <p className="text-sm mb-0">
            {uploading ? t('Snags_modal_files_uploading') : t('Snags_modal_files_drag_or_click_images')}
          </p>
        )}
        <p className="text-xs text-muted-foreground mb-0">{t('Snags_modal_files_images_only')}</p>
      </div>

      {/* Upload progress */}
      {uploading && Object.keys(uploads).length > 0 && (
        <div className="space-y-2">
          {Object.values(uploads).map((uploadInfo: any, index) => (
            <div key={index}>
              <div className="flex justify-between text-xs mb-1">
                <span>{uploadInfo.name}</span>
                <span>{uploadInfo.loaded} / {uploadInfo.total}</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${uploadInfo.progress}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image grid */}
      {files.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-3">
          {files.map((file) => (
            <div key={file.id} className="group relative rounded-md border overflow-hidden bg-muted/30">
              <div
                className="relative w-full pb-[100%] cursor-pointer overflow-hidden"
                onClick={() => setPreviewImage(file)}
              >
                <img
                  src={getFileUrl(file.id)}
                  alt={file.filename}
                  className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
              <div className="p-2">
                <p className="text-xs truncate mb-0" title={file.filename}>{file.filename}</p>
                <p className="text-[10px] text-muted-foreground mb-0">{formatBytes(file.size)}</p>
              </div>
              {!disabled && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-90 transition-opacity"
                  onClick={() => handleDeleteFile(file.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center">{t('Snags_modal_files_no_files')}</p>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={(open) => { if (!open) setPreviewImage(null) }}>
        <DialogContent className="sm:max-w-[90vw] p-0 overflow-hidden">
          {previewImage && (
            <>
              <img
                src={getFileUrl(previewImage.id)}
                alt={previewImage.filename}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
              <div className="p-4 border-t">
                <p className="font-medium text-sm mb-0">{previewImage.filename}</p>
                <p className="text-xs text-muted-foreground mb-0">{formatBytes(previewImage.size)}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SnagFileUpload
